import parse, { HTMLReactParserOptions, Element, DOMNode } from 'html-react-parser';
import sanitize from 'sanitize-html';
import { useLocation } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';
import { cx } from '@emotion/css';

import { StyleLibrary } from '@vm/omega-shared-component-webapp';
const { ComponentStyles } = StyleLibrary;

import { PrimaryButton } from '../shared/Buttons/PrimaryButton';

export const parseHtml = (html: string, sanitizeOptions?: sanitize.IOptions, parserOptions?): ReturnType<typeof parse> => {
  // replace any anchor link (<a>) that has a document fragment (#) as a link destination
  const parseOptions: HTMLReactParserOptions = {
    replace: (domNode: DOMNode) => {
      if (domNode instanceof Element && domNode?.name === 'a') {
        if (domNode?.attribs?.href.startsWith('#') && domNode.children.length) {
          const anchorChildNode = domNode.firstChild;
          if (!anchorChildNode) return;
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const anchorText = anchorChildNode?.data; // Raw JS shows the data key, but this doesn't come through as a non-abstract type definition
          const { pathname, search } = useLocation();
          return <HashLink to={`${pathname}${search}${domNode?.attribs?.href}`}>{anchorText}</HashLink>;
        }
      }

      // all modal content comes from MS Dynamics
      if (domNode instanceof Element && domNode.attribs) {
        if (domNode.attribs.id === 'cta-modal-continue') {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const btnText = domNode.children[0]?.data;
          return (
            <PrimaryButton id='cta-modal-continue' data-testid='cta-modal-continue'>
              {btnText}
            </PrimaryButton>
          );
        }
        if (domNode.attribs.id === 'cta-learn-more') {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const linkText = domNode.children[0]?.data;
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const ctaText = domNode.children[0]?.parent.attribs.href;
          return (
            <a href={ctaText} className={cx(ComponentStyles.ButtonStyles.secondaryButton)}>
              {linkText}
            </a>
          );
        }
      }
    },
  };

  const defaultSanitizeOptions: sanitize.IOptions = {
    allowedClasses: {
      a: ['bg-dark'],
      td: ['tick', 'cross'],
      span: ['sr-only'],
      div: ['table-wrapper', 'cta-modal'],
    },
    allowedAttributes: {
      a: ['href', 'style', 'target', 'id'],
      td: ['class'],
      th: ['scope'],
      tr: ['scope'],
      button: ['class', 'data-testid', 'id'],
    },
    allowedTags: sanitize.defaults.allowedTags.concat(['button']),
    transformTags: {
      a: (tagName, attribs) => ({
        tagName,
        attribs: {
          target: '_blank',
          rel: 'noreferrer noopener',
          ...attribs,
        },
      }),
    },
  };
  return parse(sanitize(html, sanitizeOptions ? sanitizeOptions : defaultSanitizeOptions), parseOptions);
};

export const removeHtmlFromString = (html: string): string => {
  const sanitized = sanitize(html, {
    allowedTags: ['br'],
  });
  return sanitized.replace('<br />', ' ');
};
